import {
  AppInterface,
  CallEventActorType,
  CallEventInterface,
  CallInterface,
  CallRecordingResponse,
  CallRecordingsListQueryParams,
  CursorPaginatedResponse,
  PaginatedResponse,
  SessionInterface
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/server/src/API/Calls/Calls.Controller.ts

export interface GetCallRecordingsParams {
  locationId?: number;
  after?: string;
  before?: string;
  limit?: number;
  sort?: string[];
}

export class CallsAPI {
  static async getCallRecordingsByOrg(orgId: number, params: GetCallRecordingsParams = {}): Promise<CursorPaginatedResponse<CallRecordingResponse>> {
    const queryParams = new URLSearchParams();
    if (params.locationId !== undefined) queryParams.append("locationId", String(params.locationId));
    if (params.after) queryParams.append("after", params.after);
    if (params.before) queryParams.append("before", params.before);
    if (params.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params.sort) params.sort.forEach((s) => queryParams.append("sort", s));

    const queryString = queryParams.toString();
    const url = `/calls/org/${orgId}/recordings${queryString ? `?${queryString}` : ""}`;
    return APIService.Server.get<CursorPaginatedResponse<CallRecordingResponse>>(url);
  }

  static async getCallRecordingsListByOrg(
    orgId: number,
    params: CallRecordingsListQueryParams = {}
  ): Promise<PaginatedResponse<CallRecordingResponse>> {
    const queryParams = new URLSearchParams();
    if (params.locationId !== undefined) queryParams.append("locationId", String(params.locationId));
    if (params.offset !== undefined) queryParams.append("offset", String(params.offset));
    if (params.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params.sort) params.sort.forEach((s) => queryParams.append("sort", s));
    if (params.search) queryParams.append("search", params.search);
    if (params.userId !== undefined) queryParams.append("userId", String(params.userId));

    const queryString = queryParams.toString();
    const url = `/calls/org/${orgId}/recordings/list${queryString ? `?${queryString}` : ""}`;
    return APIService.Server.get<PaginatedResponse<CallRecordingResponse>>(url);
  }

  static async getCallRecordingById(orgId: number, callId: number): Promise<CallRecordingResponse | null> {
    return APIService.Server.get<CallRecordingResponse | null>(`/calls/org/${orgId}/recordings/${callId}`);
  }

  static async getCallRecordingByCallSid(callSid: string): Promise<CallRecordingResponse | null> {
    return APIService.Server.get<CallRecordingResponse | null>(`/calls/recordings/${callSid}`);
  }

  static async createOrUpdateCall(
    callSid: string,
    body: {
      app?: AppInterface;
      session?: SessionInterface;
      callStatusBody?: any;
      recordingStatusBody?: any;
      voiceCallStartedBody?: any;
      amdStatusBody?: any;
      initiatorMemberId?: number;
      conferenceSid?: string;
    }
  ): Promise<CallInterface | null> {
    return APIService.Server.put<CallInterface | null>(`/calls/${callSid}/`, body);
  }

  static async getCallByCallSid(callSid: string): Promise<CallInterface | null> {
    return APIService.Server.get<CallInterface | null>(`/calls/${callSid}`);
  }

  /**
   * Log a call event. This method catches errors internally to ensure
   * event logging failures don't break the main call flow.
   */
  static async logEvent(body: {
    orgId: number;
    callSid: string;
    eventName: string;
    actorType: CallEventActorType;
    actorUserId?: number;
    actorMemberId?: number;
    targetType?: CallEventActorType;
    targetUserId?: number;
    targetMemberId?: number;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      await APIService.Server.post<CallEventInterface>(`/calls/${body.callSid}/events`, body);
    } catch (error) {
      console.warn("Failed to log call event", { eventName: body.eventName, callSid: body.callSid, error });
    }
  }
}
